import type { Dispatch, SetStateAction } from 'react';
import { memo, useRef } from 'react';

import type { StyleType } from '../types';

import * as S from './EngravingInputs.styles';

export interface EngravingDateInputProps {
  dates: string[];
  setDates: Dispatch<SetStateAction<string[]>>;
  style: StyleType;
}

const EngravingDateInputComponent = ({
  dates,
  setDates,
  style,
}: EngravingDateInputProps) => {
  const sanitizeDate = (value: string): string => {
    const numOnly = value.replace(/[^0-9]/g, '');
    return numOnly.slice(0, 1);
  };

  const inputsRef = useRef<Array<HTMLInputElement | null>>([
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
  ]);

  const handleDateChange =
    (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const sanitized = sanitizeDate(e.target.value);
      setDates((prev) => {
        const next = [...prev];
        next[index] = sanitized;
        return next;
      });

      if (sanitized && index < inputsRef.current.length - 1) {
        setTimeout(() => {
          const nextInput = inputsRef.current[index + 1];
          nextInput?.focus();
        }, 0);
      }
    };

  const handleDateBeforeInput =
    (index: number) => (e: React.FormEvent<HTMLInputElement>) => {
      const native = e.nativeEvent as unknown as InputEvent;
      const data = native?.data ?? '';

      if (!data) return;

      if (!/^\d$/.test(data)) {
        e.preventDefault();
        return;
      }

      e.preventDefault();
      setDates((prev) => {
        const next = [...prev];
        next[index] = data;
        return next;
      });

      if (index < inputsRef.current.length - 1) {
        setTimeout(() => {
          const nextInput = inputsRef.current[index + 1];
          nextInput?.focus();
        }, 0);
      } else {
        setTimeout(() => {
          const current = inputsRef.current[index];
          current?.focus();
        }, 0);
      }
    };

  const handleKeyDown =
    (index: number) => (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace') {
        const currentVal = dates[index] ?? '';
        if (currentVal === '' && index > 0) {
          e.preventDefault();
          setDates((prev) => {
            const next = [...prev];
            const prevIndex = index - 1;
            next[prevIndex] = '';
            return next;
          });
          setTimeout(() => {
            inputsRef.current[index - 1]?.focus();
          }, 0);
        }
      }
    };

  const handlePaste =
    (startIndex: number) => (e: React.ClipboardEvent<HTMLInputElement>) => {
      const text = e.clipboardData.getData('text') || '';
      const digitsOnly = text.replace(/[^0-9]/g, '');
      if (!digitsOnly) return;
      e.preventDefault();
      setDates((prev) => {
        const next = [...prev];
        let cursor = startIndex;
        for (
          let i = 0;
          i < digitsOnly.length && cursor < inputsRef.current.length;
          i += 1
        ) {
          next[cursor] = digitsOnly[i];
          cursor += 1;
        }
        return next;
      });
      setTimeout(() => {
        const endIndex = Math.min(
          startIndex + digitsOnly.length - 1,
          inputsRef.current.length - 1,
        );
        inputsRef.current[endIndex]?.focus();
      }, 0);
    };

  return (
    <S.Wrapper>
      <S.CharacterBox>
        <S.CharacterInput
          ref={(el) => {
            inputsRef.current[0] = el;
          }}
          $isDate
          $style={style}
          inputMode='numeric'
          onBeforeInput={handleDateBeforeInput(0)}
          onChange={handleDateChange(0)}
          onKeyDown={handleKeyDown(0)}
          onPaste={handlePaste(0)}
          pattern='[0-9]'
          value={dates[0]}
        />
      </S.CharacterBox>

      <S.CharacterBox $withSeparater={true}>
        <S.CharacterInput
          ref={(el) => {
            inputsRef.current[1] = el;
          }}
          $isDate
          $style={style}
          inputMode='numeric'
          onBeforeInput={handleDateBeforeInput(1)}
          onChange={handleDateChange(1)}
          onKeyDown={handleKeyDown(1)}
          onPaste={handlePaste(1)}
          pattern='[0-9]'
          value={dates[1]}
        />
      </S.CharacterBox>

      <S.CharacterBox>
        <S.CharacterInput
          ref={(el) => {
            inputsRef.current[2] = el;
          }}
          $isDate
          $style={style}
          inputMode='numeric'
          onBeforeInput={handleDateBeforeInput(2)}
          onChange={handleDateChange(2)}
          onKeyDown={handleKeyDown(2)}
          onPaste={handlePaste(2)}
          pattern='[0-9]'
          value={dates[2]}
        />
      </S.CharacterBox>

      <S.CharacterBox $withSeparater={true}>
        <S.CharacterInput
          ref={(el) => {
            inputsRef.current[3] = el;
          }}
          $isDate
          $style={style}
          inputMode='numeric'
          onBeforeInput={handleDateBeforeInput(3)}
          onChange={handleDateChange(3)}
          onKeyDown={handleKeyDown(3)}
          onPaste={handlePaste(3)}
          pattern='[0-9]'
          value={dates[3]}
        />
      </S.CharacterBox>

      <S.CharacterBox>
        <S.CharacterInput
          ref={(el) => {
            inputsRef.current[4] = el;
          }}
          $isDate
          $style={style}
          inputMode='numeric'
          onBeforeInput={handleDateBeforeInput(4)}
          onChange={handleDateChange(4)}
          onKeyDown={handleKeyDown(4)}
          onPaste={handlePaste(4)}
          pattern='[0-9]'
          value={dates[4]}
        />
      </S.CharacterBox>

      <S.CharacterBox>
        <S.CharacterInput
          ref={(el) => {
            inputsRef.current[5] = el;
          }}
          $isDate
          $style={style}
          inputMode='numeric'
          onBeforeInput={handleDateBeforeInput(5)}
          onChange={handleDateChange(5)}
          onKeyDown={handleKeyDown(5)}
          onPaste={handlePaste(5)}
          pattern='[0-9]'
          value={dates[5]}
        />
      </S.CharacterBox>

      <S.CharacterBox>
        <S.CharacterInput
          ref={(el) => {
            inputsRef.current[6] = el;
          }}
          $isDate
          $style={style}
          inputMode='numeric'
          onBeforeInput={handleDateBeforeInput(6)}
          onChange={handleDateChange(6)}
          onKeyDown={handleKeyDown(6)}
          onPaste={handlePaste(6)}
          pattern='[0-9]'
          value={dates[6]}
        />
      </S.CharacterBox>

      <S.CharacterBox>
        <S.CharacterInput
          ref={(el) => {
            inputsRef.current[7] = el;
          }}
          $isDate
          $style={style}
          inputMode='numeric'
          onBeforeInput={handleDateBeforeInput(7)}
          onChange={handleDateChange(7)}
          onKeyDown={handleKeyDown(7)}
          onPaste={handlePaste(7)}
          pattern='[0-9]'
          value={dates[7]}
        />
      </S.CharacterBox>
    </S.Wrapper>
  );
};

export const EngravingDateInput = memo(EngravingDateInputComponent);
